import React, { useState, useEffect } from "react";
import { Checkbox, Slider, Button, Collapse } from "antd";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
import api from "../services/api";
import '../styles/SearchFilter.css';

interface Industry {
  id: number;
  name: string;
}

interface Software {
  id: number;
  name: string;
}

interface FilterValues {
  industries: number[];
  software: number[];
  questionRange: [number, number];
  timeLimit: {
    timed: boolean;
    timeRange: [number, number];
    noTimeLimit: boolean;
  };
}

interface Props {
  onFilterApply: (filters: FilterValues) => void;
  maxQuestions: number;
  maxTimeLimit: number;
}

const TestFilter: React.FC<Props> = ({ onFilterApply, maxQuestions, maxTimeLimit }) => {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [software, setSoftware] = useState<Software[]>([]);
  const [filters, setFilters] = useState<FilterValues>({
    industries: [],
    software: [],
    questionRange: [0, maxQuestions || 50],
    timeLimit: {
      timed: false,
      timeRange: [10, maxTimeLimit || 90],
      noTimeLimit: false,
    }
  });

  useEffect(() => {
    fetchIndustries();
    fetchSoftware();
  }, []);

  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      questionRange: [0, maxQuestions || 50],
      timeLimit: {
        ...prev.timeLimit,
        timeRange: [10, maxTimeLimit || 90]
      }
    }));
  }, [maxQuestions, maxTimeLimit]);

  useEffect(() => {
    onFilterApply(filters);
  }, [filters]);

  const fetchIndustries = async () => {
    try {
      const response = await api.get('industries/');
      setIndustries(response.data);
    } catch (error) {
      console.error('Failed to fetch industries:', error);
      setIndustries([]);
    }
  };

  const fetchSoftware = async () => {
    try {
      const response = await api.get('software/');
      setSoftware(response.data);
    } catch (error) {
      console.error('Failed to fetch software:', error);
      setSoftware([]);
    }
  };

  const handleIndustryChange = (industryId: number, checked: boolean) => {
    const updatedIndustries = checked
      ? [...filters.industries, industryId]
      : filters.industries.filter(id => id !== industryId);

    setFilters({
      ...filters,
      industries: updatedIndustries
    });
  };

  const handleSoftwareChange = (softwareId: number, checked: boolean) => {
    const updatedSoftware = checked
      ? [...filters.software, softwareId]
      : filters.software.filter(id => id !== softwareId);

    setFilters({
      ...filters,
      software: updatedSoftware
    });
  };

  const handleQuestionRangeChange = (value: number | number[]) => {
    const range: [number, number] = Array.isArray(value) ? [value[0], value[1]] : [5, value as number];
    setFilters({
      ...filters,
      questionRange: range
    });
  };

  const handleTimeLimitChange = (type: 'timed' | 'noTimeLimit', checked: boolean) => {
    setFilters({
      ...filters,
      timeLimit: {
        ...filters.timeLimit,
        [type]: checked
      }
    });
  };

  const handleTimeRangeChange = (value: number | number[]) => {
    const range: [number, number] = Array.isArray(value) ? [value[0], value[1]] : [10, value as number];
    setFilters({
      ...filters,
      timeLimit: {
        ...filters.timeLimit,
        timeRange: range
      }
    });
  };

  const clearIndustryFilters = () => {
    setFilters({
      ...filters,
      industries: []
    });
  };

  const clearSoftwareFilters = () => {
    setFilters({
      ...filters,
      software: []
    });
  };

  const clearQuestionFilters = () => {
    setFilters({
      ...filters,
      questionRange: [0, maxQuestions || 50] as [number, number]
    });
  };

  const clearTimeLimitFilters = () => {
    setFilters({
      ...filters,
      timeLimit: {
        timed: false,
        timeRange: [10, maxTimeLimit || 90] as [number, number],
        noTimeLimit: false,
      }
    });
  };

  const clearAllFilters = () => {
    const resetFilters = {
      industries: [],
      software: [],
      questionRange: [0, maxQuestions || 50] as [number, number],
      timeLimit: {
        timed: false,
        timeRange: [10, maxTimeLimit || 90] as [number, number],
        noTimeLimit: false,
      }
    };
    setFilters(resetFilters);
  };

  return (
    <div style={containerStyle} className="search-filter">
      {/* Header */}
      <div style={headerStyle}>
        <h3 style={titleStyle}>Filters</h3>
        <Button type="link" onClick={clearAllFilters} style={clearAllStyle}>
          Clear all
        </Button>
      </div>

      {/* Filters */}
      <Collapse
        defaultActiveKey={['industries', 'questions', 'timeLimit']}
        ghost
        expandIconPosition="end"
        expandIcon={({ isActive }) => isActive ? <UpOutlined /> : <DownOutlined />}
        items={[
          // Industries Filter
          {
            key: 'industries',
            label: 'Industries',
            style: panelStyle,
            children: (
              <>
                <div style={checkboxGroupStyle}>
                  {industries.map(industry => (
                    <div key={industry.id} style={checkboxItemStyle}>
                      <Checkbox
                        checked={filters.industries.includes(industry.id)}
                        onChange={(e) => handleIndustryChange(industry.id, e.target.checked)}
                        style={checkboxStyle}
                      >
                        {industry.name}
                      </Checkbox>
                    </div>
                  ))}
                </div>
                <Button type="link" onClick={clearIndustryFilters} style={clearFiltersStyle}>
                  Clear filters
                </Button>
              </>
            )
          },
          // Software Filter
          {
            key: 'software',
            label: 'Software',
            style: panelStyle,
            children: (
              <>
                <div style={checkboxGroupStyle}>
                  {software.map(softwareItem => (
                    <div key={softwareItem.id} style={checkboxItemStyle}>
                      <Checkbox
                        checked={filters.software.includes(softwareItem.id)}
                        onChange={(e) => handleSoftwareChange(softwareItem.id, e.target.checked)}
                        style={checkboxStyle}
                      >
                        {softwareItem.name}
                      </Checkbox>
                    </div>
                  ))}
                </div>
                <Button type="link" onClick={clearSoftwareFilters} style={clearFiltersStyle}>
                  Clear filters
                </Button>
              </>
            )
          },
          // Questions Filter
          {
            key: 'questions',
            label: 'Questions',
            style: panelStyle,
            children: (
              <>
                <div style={sliderContainerStyle}>
                  <Slider
                    range
                    min={0}
                    max={maxQuestions || 50}
                    value={filters.questionRange}
                    onChange={handleQuestionRangeChange}
                    style={sliderStyle}
                    trackStyle={[{ backgroundColor: '#1677ff' }]}
                    handleStyle={[
                      { backgroundColor: '#1677ff', borderColor: '#1677ff' },
                      { backgroundColor: '#1677ff', borderColor: '#1677ff' }
                    ]}
                  />
                  <div style={rangeLabelsStyle}>
                    <span>{filters.questionRange[0]}</span>
                    <span>{filters.questionRange[1]}</span>
                  </div>
                </div>
                <Button type="link" onClick={clearQuestionFilters} style={clearFiltersStyle}>
                  Clear filters
                </Button>
              </>
            )
          },
          // Time Limit Filter
          {
            key: 'timeLimit',
            label: 'Time limit',
            style: panelStyle,
            children: (
              <>
                <div style={checkboxGroupStyle}>
                  <div style={checkboxItemStyle}>
                    <Checkbox
                      checked={filters.timeLimit.timed}
                      onChange={(e) => handleTimeLimitChange('timed', e.target.checked)}
                      style={checkboxStyle}
                    >
                      Timed
                    </Checkbox>
                  </div>
                  {filters.timeLimit.timed && (
                    <div style={sliderContainerStyle}>
                      <Slider
                        range
                        min={10}
                        max={maxTimeLimit || 90}
                        value={filters.timeLimit.timeRange}
                        onChange={handleTimeRangeChange}
                        style={sliderStyle}
                        trackStyle={[{ backgroundColor: '#1677ff' }]}
                        handleStyle={[
                          { backgroundColor: '#1677ff', borderColor: '#1677ff' },
                          { backgroundColor: '#1677ff', borderColor: '#1677ff' }
                        ]}
                      />
                      <div style={rangeLabelsStyle}>
                        <span>{filters.timeLimit.timeRange[0]}:00</span>
                        <span>{filters.timeLimit.timeRange[1]}:00</span>
                      </div>
                    </div>
                  )}
                  <div style={checkboxItemStyle}>
                    <Checkbox
                      checked={filters.timeLimit.noTimeLimit}
                      onChange={(e) => handleTimeLimitChange('noTimeLimit', e.target.checked)}
                      style={checkboxStyle}
                    >
                      No time limit
                    </Checkbox>
                  </div>
                </div>
                <Button type="link" onClick={clearTimeLimitFilters} style={clearFiltersStyle}>
                  Clear filters
                </Button>
              </>
            )
          }
        ]}
      />
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: 8,
  padding: '20px',
  minHeight: 'fit-content',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
};

const titleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  color: '#1a1a1a',
  margin: 0,
};

const clearAllStyle: React.CSSProperties = {
  color: '#1677ff',
  padding: 0,
  fontSize: 14,
  fontWeight: 500,
};

const panelStyle: React.CSSProperties = {

};

const checkboxGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginBottom: '16px',
};

const checkboxItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
};

const checkboxStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#333',
};

const sliderContainerStyle: React.CSSProperties = {
  padding: '0 8px',
  marginBottom: '16px',
};

const sliderStyle: React.CSSProperties = {
  margin: '16px 0',
};

const rangeLabelsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 12,
  color: '#666',
  marginTop: 8,
};

const clearFiltersStyle: React.CSSProperties = {
  color: '#1677ff',
  padding: 0,
  fontSize: 14,
  fontWeight: 500,
  marginTop: 8,
};

export default TestFilter;